import React, {useCallback, useEffect, useState} from 'react';
import {
  View,
  Keyboard,
  TouchableWithoutFeedback,
  Text,
  TouchableOpacity,
  Pressable,
} from 'react-native';

import {ScrollView} from 'react-native-gesture-handler';

import {
  useFocusEffect,
  useNavigation,
} from '@react-navigation/native';
import {width, height, totalSize} from 'react-native-dimension';
import {Icon, Overlay} from 'react-native-elements';
import {NavigationKey} from '../../navigations/config';

import {StyleSheet} from 'react-native';
import {colorPalette} from '../../../core/config/color.config';

import {RNEButton, RNEText, SVGIcon} from '../../components';

import useStores from '../../stores/useStores';

import {
  useGetHistoryConsultation,
  useCancelConsultationDetail,
  useCancelBulkConsultationDetail,
} from '../../server/react-query/useConsultation';
import {
  ConsultationRequestResBody,
  ConsultationStatusEnum,
} from '../../server/types/consultation.types';

import ConsultationListItemHistory from '../consultation/ConsultationListItemHistory';
import RadioButton from '../../components/RadioButtons/RadionButtons.native';
import CloseIcon from '../../../assets/icons/svg/close-red.svg';
import ConsultationCancelConfirmationModal from '../modal/ConsultationCancelConfirmationModal';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';
import {useMarkNotificationsReadByConsultationStatus} from '../../hooks/notification/useMarkNotificationsReadByConsultationStatus';

interface ConsultationHistoryTabProps {
  searchKey?: string;
}

const ConsultationHistoryTab = (props: ConsultationHistoryTabProps) => {
  const navigation = useNavigation();

  const {authStore, patientProfileStore, recentlyViewedAccountsStore} =
    useStores();
  const activeProfile = patientProfileStore.activeProfileData;
  const {canRefresh, setCanRefresh} = usePullDownRefreshContext();
  useMarkNotificationsReadByConsultationStatus('history');

  const [showRadioButtons, setShowRadioButtons] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isArchiveConfirmModalVisible, setIsArchiveConfirmModalVisible] =
    useState(false);
  const [singleArchiveSelection, setSingleArchiveSelection] = useState();
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const {data, isLoading} = useGetHistoryConsultation({
    status: props.searchKey
      ? [ConsultationStatusEnum.COMPLETED]
      : [ConsultationStatusEnum.COMPLETED, ConsultationStatusEnum.DECLINED],
    subject: activeProfile?.patientId,
    SortBy: 'modifiedOn',
    Ascending: false,
    searchKey: props.searchKey,
  });

  const archieveMutation = useCancelConsultationDetail({
    serviceRequestId: singleArchiveSelection?.serviceRequestId,
    status: ConsultationStatusEnum.ARCHIVED,
  });
  const bulkArchieveMutation = useCancelBulkConsultationDetail({
    serviceRequestIds: selectedIds,
    status: ConsultationStatusEnum.ARCHIVED,
    note: '',
  });

  const onItemPressed = (
    id: string,
    consultationData: ConsultationRequestResBody,
  ) => {
    recentlyViewedAccountsStore.addRecentlyViewedAccount(id, consultationData);
    (navigation as any).navigate(NavigationKey.CONSULTATION_DETAIL, {id});
  };

  const onRadioSelected = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  };

  const handleArchiveRequest = async () => {
    try {
      setIsBtnLoading(true);
      await archieveMutation.mutateAsync();
      setIsArchiveConfirmModalVisible(false);
      setTimeout(() => {
        setIsBtnLoading(false);
      }, 1000);
    } catch (error) {
      setIsArchiveConfirmModalVisible(false);
      setIsBtnLoading(false);
    }
  };

  const handleBulkArchiveRequest = async () => {
    try {
      setIsBtnLoading(true);
      await bulkArchieveMutation.mutateAsync();
      setIsArchiveConfirmModalVisible(false);
      setSelectedIds([]);
      setShowRadioButtons(false);
      setTimeout(() => {
        setIsBtnLoading(false);
      }, 1000);
    } catch (error) {
      setIsBtnLoading(false);
    }
  };

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      // setCanRefresh(false);
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );

  useEffect(() => {
    if (scrollY > 0) {
      setCanRefresh(false);
    } else if (scrollY <= 0) {
      setCanRefresh(true);
    }
  }, [scrollY]);

  const handleScroll = (e: any) => {
    setCanRefresh(false);
    // const isAtTop = e.nativeEvent.contentOffset.y > 0;
    // if(isAtTop) return false;

    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };
  

  return (
    <>
      <ScrollView
        bounces={false}
        onScroll={handleScroll}
        onLayout={e => {
          setScrollViewHeight(e.nativeEvent.layout.height);
        }}
        onContentSizeChange={(contentWidth, contentHeight) => {
          setContentHeight(contentHeight);
        }}
        contentContainerStyle={[
          {flexGrow:1},
          showRadioButtons && {paddingBottom: height(18)},
           data && data.length === 0 && {justifyContent: 'center'},
        ]}>
        {isLoading && (
          <View style={styles.spinnerContainer}>
            <RNEButton
              height={totalSize(4)}
              loadingProps={loadingPropsStyle}
              loading={isLoading}
            />
          </View>
        )}

        {authStore.userRole === 'practitioner' &&
          !showRadioButtons &&
          !isLoading &&
          data && data.length > 1 && (
            <View style={{marginTop: height(1)}}>
              <Text
                style={{
                  textAlign: 'center',
                  fontFamily: 'Quicksand-SemiBold',
                  color: colorPalette.grey,
                  fontSize: totalSize(1.5),
                }}>
                Long press to select multiple requests.
              </Text>
            </View>
          )}

        {data &&
          data.map((item, index) => (
            <ConsultationListItemHistory
              key={index}
              item={item}
              onPress={() => onItemPressed(item.serviceRequestId, item)}
              showRadioButtons={showRadioButtons}
              onEnableRadioButtons={() => setShowRadioButtons(true)}
              onRadioSelected={() => onRadioSelected(item.serviceRequestId)}
              selectedRadio={selectedIds}
              isArchiveConfirmModalVisible={isArchiveConfirmModalVisible}
              setIsArchiveConfirmModalVisible={setIsArchiveConfirmModalVisible}
              setSingleArchiveSelection={setSingleArchiveSelection}
              selectedIds={selectedIds}
            />
          ))}

        {data && data.length === 0 && (
          <View style={styles.placeholder}>
            <RNEText
              label={props.searchKey ? '' : 'No consultation history.'}
              color="grey"
              style={{fontSize: totalSize(1.8), fontFamily: 'Quicksand-Medium'}}
            />
          </View>
        )}
      </ScrollView>
      {showRadioButtons && (
        <View style={styles.archiveButtonContainer}>
          <Pressable
            onPress={() => setIsArchiveConfirmModalVisible(true)}
            disabled={selectedIds?.length === 0}>
            <View
              style={[
                styles.container,
                styles.archiveContainer,
                selectedIds?.length === 0 && {
                  backgroundColor: 'gray',
                  borderColor: 'gray',
                },
              ]}>
              <View style={styles.btnContainer}>
                <Text style={styles.btnTxtStyle}>Archive</Text>
              </View>
            </View>
          </Pressable>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={() => {
              setShowRadioButtons(false);
              setSelectedIds([]);
            }}>
            <SVGIcon name="close-red" size={totalSize(5)} />
            {/* <CloseIcon /> */}
          </TouchableOpacity>
        </View>
      )}
      <Overlay
        isVisible={isArchiveConfirmModalVisible}
        overlayStyle={styles.overlayContainer}>
        <ConsultationCancelConfirmationModal
          isLoading={isBtnLoading}
          title={
            showRadioButtons
              ? 'Are you sure you want to archive all the selected consultation?'
              : 'Are you sure you want to archive this consultation?'
          }
          primaryBtnText={'Yes'}
          secondaryBtnText={'No'}
          onConfirm={
            showRadioButtons ? handleBulkArchiveRequest : handleArchiveRequest
          }
          onCancel={() => {
            setIsArchiveConfirmModalVisible(!isArchiveConfirmModalVisible);
            setSelectedIds([]);
            setShowRadioButtons(false);
          }}
        />
      </Overlay>
    </>
  );
};

const loadingPropsStyle = {
  color: colorPalette.black,
  size: totalSize(3.5),
};

const styles = StyleSheet.create({
  spinnerContainer: {
    marginTop: totalSize(5),
    width: '100%',
    justifyContent: 'flex-end',
  },
  placeholder: {
    // flex: 1,
    // alignItems: 'center',
    // justifyContent: 'center',
    // minHeight: height(70),
    flex: 1, // take full available height
    alignItems: 'center', // center horizontally
    justifyContent: 'center', // center vertically
  },
  archivebutton: {
    backgroundColor: '#1E9CE4',
    borderRadius: 30, // pill shape
    paddingVertical: 12,
    paddingHorizontal: 50,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowOffset: {width: 0, height: 2},
    shadowRadius: 3,
    elevation: 3,
  },
  archivecontent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  archivetext: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  archiveButtonContainer: {
    // flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: totalSize(1),
    position: 'absolute',
    bottom: 20,
    alignSelf: 'center',
    zIndex: 1000,
  },
  closeButton: {
    top: 4,
  },
  overlayContainer: {
    borderRadius: totalSize(2.5),
    marginHorizontal: 40,
    width: width(90),
    borderColor: 'black',
    borderWidth: totalSize(0.15),
  },
  container: {
    borderWidth: totalSize(0.2),
    padding: totalSize(1.5),
    paddingTop: totalSize(1),
    paddingBottom: totalSize(1),
    paddingRight: width(10.5),
    paddingLeft: width(10.5),
    borderRadius: totalSize(5),
    alignSelf: 'center',
  },
  archiveContainer: {
    borderColor: '#3FB4E6',
    backgroundColor: '#3FB4E6',
  },
  btnContainer: {
    minWidth: width(14),
    maxWidth: width(14),
  },
  btnTxtStyle: {
    textAlign: 'center',
    color: colorPalette.white,
    fontFamily: 'Quicksand-Bold',
    fontSize: totalSize(1.3),
  },
});

export default ConsultationHistoryTab;
